import * as React from "react";
import { IKnowledgeBaseArticle } from "../api/knowledgeBase";

type SelectArticle = (
  articleId: number | null,
  options?: { fromHash?: boolean }
) => void;

type UpdateHash = (
  articleId: number | null,
  options?: { slugOverride?: string }
) => void;

export function useArticleEditor({
  selectedArticle,
  allArticles,
  selectedArticleId,
  selectArticle,
  updateHash,
  isCreatingRef,
}: {
  selectedArticle: IKnowledgeBaseArticle | null;
  allArticles: IKnowledgeBaseArticle[] | undefined;
  selectedArticleId: number | null;
  selectArticle: SelectArticle;
  updateHash: UpdateHash;
  isCreatingRef: React.MutableRefObject<boolean>;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState("");
  const [editSlug, setEditSlug] = React.useState("");
  const [createParentId, setCreateParentId] = React.useState<number | null>(
    null
  );
  const [createPosition, setCreatePosition] = React.useState<number | null>(
    null
  );
  const [previousSelectedId, setPreviousSelectedId] = React.useState<
    number | null
  >(null);

  React.useEffect(() => {
    if (isEditing) return;

    if (selectedArticle) {
      setEditTitle(selectedArticle.title);
      setEditSlug(selectedArticle.slug || "");
    } else {
      setEditTitle("");
      setEditSlug("");
    }
  }, [isEditing, selectedArticle]);

  React.useEffect(() => {
    isCreatingRef.current = isCreating;
  }, [isCreating, isCreatingRef]);

  const computeCreatePlacement = React.useCallback(
    (targetParentId?: number | null) => {
      const articles = allArticles ?? [];
      const rootArticles = articles
        .filter((article) => article.parent_id === null)
        .sort((a, b) => a.position - b.position);

      if (targetParentId !== undefined) {
        if (targetParentId === null) {
          return { parentId: null, position: rootArticles.length };
        }

        const childArticles = articles
          .filter((article) => article.parent_id === targetParentId)
          .sort((a, b) => a.position - b.position);

        return {
          parentId: targetParentId,
          position: 0,
        };
      }

      if (!selectedArticleId) {
        return { parentId: null, position: rootArticles.length };
      }

      const selected = articles.find(
        (article) => article.id === selectedArticleId
      );

      if (!selected) {
        return { parentId: null, position: rootArticles.length };
      }

      const selectedChildren = articles
        .filter((article) => article.parent_id === selected.id)
        .sort((a, b) => a.position - b.position);

      if (selectedChildren.length > 0) {
        return { parentId: selected.id, position: 0 };
      }

      const parentId = selected.parent_id;
      const siblings = articles
        .filter((article) => article.parent_id === parentId)
        .sort((a, b) => a.position - b.position);
      const selectedSibling = siblings.find(
        (article) => article.id === selected.id
      );
      const nextPosition =
        selectedSibling !== undefined
          ? selectedSibling.position + 1
          : siblings.length;

      return { parentId: parentId ?? null, position: nextPosition };
    },
    [allArticles, selectedArticleId]
  );

  const resetForSelection = React.useCallback(() => {
    setIsCreating(false);
    setIsEditing(false);
    setCreateParentId(null);
    setCreatePosition(null);
    setPreviousSelectedId(null);
  }, []);

  const startEdit = React.useCallback(() => {
    if (!selectedArticle) return;
    setIsCreating(false);
    setCreateParentId(null);
    setCreatePosition(null);
    setEditTitle(selectedArticle.title);
    setEditSlug(selectedArticle.slug || "");
    setIsEditing(true);
  }, [selectedArticle]);

  const cancelEdit = React.useCallback(() => {
    if (isCreating) {
      setIsCreating(false);
      setIsEditing(false);
      setCreateParentId(null);
      setCreatePosition(null);
      if (previousSelectedId !== null) {
        selectArticle(previousSelectedId, { fromHash: true });
        updateHash(previousSelectedId);
      }
      setEditTitle(selectedArticle?.title || "");
      setEditSlug(selectedArticle?.slug || "");
      setPreviousSelectedId(null);
      return;
    }

    setIsEditing(false);
    if (selectedArticle) {
      setEditTitle(selectedArticle.title);
      setEditSlug(selectedArticle.slug || "");
    }
    setPreviousSelectedId(null);
  }, [
    isCreating,
    previousSelectedId,
    selectArticle,
    selectedArticle,
    updateHash,
  ]);

  const startCreate = React.useCallback(
    (parentId?: number | null) => {
      const { parentId: targetParentId, position } =
        computeCreatePlacement(parentId);
      setPreviousSelectedId(selectedArticleId);
      setIsCreating(true);
      setIsEditing(true);
      setCreateParentId(targetParentId);
      setCreatePosition(position);
      selectArticle(null, { fromHash: true });
      setEditTitle("");
      setEditSlug("");
      updateHash(null);
    },
    [computeCreatePlacement, selectArticle, selectedArticleId, updateHash]
  );

  return {
    isEditing,
    isCreating,
    editTitle,
    editSlug,
    setEditTitle,
    setEditSlug,
    startEdit,
    cancelEdit,
    startCreate,
    createParentId,
    createPosition,
    setIsEditing,
    setIsCreating,
    setCreateParentId,
    setCreatePosition,
    resetForSelection,
  };
}
